import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  type InMemoryCacheConfig,
  type Operation,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { setContext } from "@apollo/client/link/context";
import { apolloSpy } from "@shortlyster/analytics-kit";
import { getRequestId } from "@compono/sentry-browser";

import { env } from "~/env";
import { getCachedAuthContext } from "~/components/auth";
import {
  getStoredAuthedClient,
  storeAuthedClient,
  getStoredPlatformClient,
  storePlatformClient,
  getStoredPublicClient,
  storePublicClient,
} from "./clientRegistry";

// import { watcher } from "../mixpanel";

/**
 * Production cache configuration for the federation gateway client.
 *
 * Exported so tests (and callers) can build a cache with the *real* typePolicies
 * (`new InMemoryCache(createFederationCacheOptions())`).
 *
 * Note: applicant mutations reconcile eventual-consistency lag by refetching the
 * list + counts until the projection catches up (see `applicantsConvergence`),
 * not via cache merge policies — so there is no gate/merge wiring here.
 */
export const createFederationCacheOptions = (): InMemoryCacheConfig => ({
  typePolicies: {
    // there's currently two places determining stage names, which can collide and produce stages with different names but same id
    ApplicationStage: { keyFields: false },

    /**
     * Accumulated across queries rather than replaced, because three documents
     * select `profile` at deliberately different widths: the list row takes
     * `latestJob`, `cvId` and `prePopulate`, the drawer adds contact details, work
     * preferences and links, and the attachments query takes only the previews.
     *
     * `ApplicantProfile` has no id to normalise on, so Apollo's default would have
     * each write replace the last — opening the drawer over a cached row would drop
     * the previews, and a query missing a field it selected does not resolve from the
     * cache at all. Merging is what lets one entity hold all three slices.
     *
     * So this is not removable by widening the documents to agree, the way the tag
     * fragment below solved the same failure mode: agreeing here means every row of a
     * 20-row page fetching the drawer's fields. An id on `ApplicantProfile` would give
     * the same field-by-field merge for free, but it would be 1:1 with its
     * `Applicant` — a synthetic key buying what this already does.
     *
     * Shallow, so it covers `profile`'s own fields and not the un-keyed objects
     * inside it. Safe while the documents that share `latestJob` spell it identically;
     * a shared fragment is the fix if that ever stops being true.
     */
    Applicant: {
      fields: {
        profile: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },

    /**
     * Merged rather than replaced, because it has no id to normalise on and two
     * documents write it at different widths.
     *
     * A `TagActivity` is written by the tag *queries*, which read
     * `user { id firstName lastName } timestamp`, and by the tag *mutations*, whose
     * responses are narrower. Replacing — Apollo's default for an un-keyed object —
     * lets the narrower write delete fields the wider one put there, and a query
     * missing a field it selected does not resolve from the cache at all. The whole
     * listing-scoped `tagActivities` read went dark that way, which stopped every
     * row's favourite star from answering its own optimistic patch.
     *
     * Every tag document now takes that selection from one shared fragment
     * (`tagActivityActionedByUserFields`), so they cannot disagree and this changes
     * nothing today. It is here for the write that fragment doesn't reach, so a
     * narrower response costs a stale timestamp instead of a dead query.
     */
    TagActivityActionedByUser: { merge: true },

    /**
     * Merged rather than replaced, for the same reason and by the same mechanism as
     * the tag activity above: no id to normalise on, and three documents writing it
     * onto the same `Listing` at three different widths.
     *
     * `FetchListingForApplicantsPage` writes all of it; `FetchListingApplicantCounts`
     * writes `{ total }` alone for the profile route's "#rank of N"; and
     * `GetHirePipelineListings` writes `{ new, actionPending, stages, completed }` —
     * no `total` — for the pipeline board. Replacing lets whichever ran last delete
     * the fields the others put there, and a query missing a field it selected does
     * not resolve from the cache at all: opening a candidate profile and going back
     * left the page query unable to read the listing it had just fetched, so the stat
     * cards and pipeline chips blanked while it refetched.
     *
     * Widening the three to agree is the alternative, and it is the wrong one here —
     * the pipeline board would carry the applicants page's counts across every row of
     * a 50-listing page to buy nothing it renders.
     */
    ApplicantsCounts: { merge: true },
  },
});

const createAuthLink = () =>
  setContext(async (_, { headers }) => {
    const accessToken = getCachedAuthContext()?.user?.access_token;
    const orgId = getCachedAuthContext()?.orgId;
    return {
      headers: {
        ...headers,
        ...(orgId && { "x-organisation-id": orgId }),
        ...(accessToken && { authorization: `Bearer ${accessToken}` }),
        "x-request-id": getRequestId(),
      },
    };
  });

/**
 * Maximum operations Apollo will put in one batched request. Mirrors
 * `MAX_BATCHED_OPERATIONS` in the gateway runtime, which rejects anything larger
 * with a 413.
 */
const MAX_BATCHED_OPERATIONS = 10;

/**
 * Groups batchable operations by everything that has to match for them to share
 * one HTTP request, ignoring `x-request-id`.
 *
 * Required, not a tuning knob. The default `batchKey` serialises the operation's
 * whole header set, and `createAuthLink` stamps a fresh `x-request-id` on every
 * operation — so under the default no two operations ever land in the same batch
 * and batching silently does nothing at all.
 *
 * The other headers do have to match. A batch is sent as one request under one set
 * of headers, so combining operations that disagree on `authorization` or
 * `x-organisation-id` would send one of them under the other's credentials.
 *
 * A batch carries the first operation's request id and the rest lose theirs, which
 * costs gateway-side correlation only: `getRequestId()` tags the Sentry scope for
 * every operation either way.
 */
const batchKeyIgnoringRequestId =
  (uri: string) =>
  (operation: Operation): string => {
    const { "x-request-id": _requestId, ...identifyingHeaders } =
      operation.getContext().headers ?? {};

    return `${uri}${JSON.stringify(identifyingHeaders)}`;
  };

/**
 * Sends operations that ask for it (`context: { batch: true }`) as one request,
 * and everything else on its own connection.
 *
 * Opt-in rather than client-wide because a batch resolves only when its slowest
 * member does. Batching an expensive query alongside cheap metadata reads would
 * hold the cheap results back and delay first paint, even though total time drops.
 */
const createBatchingTransport = (uri: string) =>
  ApolloLink.split(
    (operation) => operation.getContext().batch === true,
    new BatchHttpLink({
      uri,
      batchMax: MAX_BATCHED_OPERATIONS,
      batchKey: batchKeyIgnoringRequestId(uri),
    }),
    new HttpLink({ uri }),
  );

const buildAuthedFederationClient = (
  uri: string,
  { batching = false }: { batching?: boolean } = {},
) => {
  const transportLink = batching
    ? createBatchingTransport(uri)
    : new HttpLink({ uri });

  const baseClient = new ApolloClient({
    link: ApolloLink.from([createAuthLink(), transportLink]),
    cache: new InMemoryCache(createFederationCacheOptions()),
  });

  // apolloSpy.configure({ watcher });
  return apolloSpy.patch(baseClient);
};

/**
 * The authed federation gateway client, as a lazy singleton (stored in the
 * dependency-free registry so tests can reset it).
 *
 * Must be a singleton, not a per-call factory: `ApolloClientsProvider` calls this
 * on every render, and a fresh client+cache per render would throw away every
 * cached query — including the one an in-flight convergence refetch is about to
 * update. Auth is read per-request inside the auth link, so a stable client stays
 * correctly authenticated.
 *
 * This one talks to the legacy `/graphql` route, which serves the whole supergraph.
 * Most of platform-ui's subgraphs carry no `@tag(name: "platform")` and so are
 * absent from the contract endpoint — see `getPlatformFederationClient`.
 *
 * No batching: that route is Apollo Server, which rejects array bodies unless
 * `allowBatchedHttpRequests` is set, and `graphql/server.ts` does not set it.
 */
export const apolloClientWithAuth0 = () => {
  const existing = getStoredAuthedClient();
  if (existing) return existing;
  const client = buildAuthedFederationClient(
    `${env.FEDERATION_GATEWAY_URL}/graphql`,
  );
  storeAuthedClient(client);
  return client;
};

/**
 * The authed client for operations that live in the `platform` GraphQL contract —
 * everything generated into `platformGraphql.ts` from `platformQueries/`.
 *
 * The contract endpoint is served from a supergraph the schema registry has already
 * composed, rather than one introspected from every subgraph on cold start. It is
 * also tag-filtered, so only operations codegen has validated against it may be sent
 * here; anything else 400s.
 *
 * Separate cache from the legacy client by construction. Tag activities are on
 * *this* client, not the legacy one — `tagQueries.tsx` lives in `platformQueries/`
 * and every call site passes `platformFederationGatewayClient` — so the applicants
 * list and the `cache.modify` in `usePatchTagActivities` share one cache, which is
 * what lets that patch reach every cached `tagActivities` variant. The separation
 * stays safe for the remaining reasons: `ApplicationStage` is `keyFields: false` so
 * it never normalises, and nothing reads an `Applicant` written by a tag query's
 * thin `targetApplicant` selection.
 *
 * Batching is available here, opt-in per operation. API Gateway spreads concurrent
 * POSTs across separate Lambda containers, each holding its own gateway runtime, so
 * an unbatched page mount pays a container acquisition — and on a cold container a
 * supergraph parse and query-planner build — once per query. The applicants page
 * batches its metadata reads for exactly that reason.
 */
export const getPlatformFederationClient = () => {
  const existing = getStoredPlatformClient();
  if (existing) return existing;
  const client = buildAuthedFederationClient(
    env.FEDERATION_GATEWAY_GRAPHQL_URL,
    { batching: true },
  );
  storePlatformClient(client);
  return client;
};

const buildPublicFederationClient = () => {
  const uri = `${env.FEDERATION_GATEWAY_URL}/publicGraphql`;

  const authLink = setContext(async (_, { headers }) => {
    return {
      headers: {
        ...headers,
        "x-request-id": getRequestId(),
      },
    };
  });
  const cache = new InMemoryCache();
  const urlLink = new HttpLink({ uri });

  const baseClient = new ApolloClient({
    link: ApolloLink.from([authLink, urlLink]),
    cache,
  });

  // apolloSpy.configure({ watcher });
  return apolloSpy.patch(baseClient);
};

/** The public (unauthed) federation gateway client, as a lazy singleton. */
export const getPublicFederationClient = () => {
  const existing = getStoredPublicClient();
  if (existing) return existing;
  const client = buildPublicFederationClient();
  storePublicClient(client);
  return client;
};
